import { RouteObject } from 'react-router';
import { Navigate, createBrowserRouter, useRouteError } from 'react-router-dom';
import { privateRoutes } from './routes/private.route';
import { publicRoutes } from './routes/public.route';
import { sadminRoutes } from './routes/sadmin.route';
import { ErrorBoundary } from './shared/components/error-boundary/error-boundary';
// ErrorBoundary is the class-based render-time catcher; RouteErrorSurface
// wraps it so React Router's `errorElement` also renders the branded UI.
import { PATH, PUBLIC_PATH } from './shared/constants/paths.constant';
import { AuthGuard } from './shared/guards/auth/auth.guard';
import { GuestGuard } from './shared/guards/guest/guest.guard';
import { SadminGuard } from './shared/guards/sadmin/sadmin.guard';
import { ShiftGateGuard } from './shared/guards/shift-gate/shift-gate.guard';
import { lazyWithRetry } from './shared/utils/lazy-with-retry';
import { createSuspendedRoute } from './shared/utils/suspended-route.utils';

// MUST be `lazyWithRetry`, never a bare React `lazy` — see the note on
// `lazy-with-retry.ts`. These are the top-level shells, so they are exactly
// the chunks a user hits when a deployment lands mid-session: the open tab
// still holds the previous `index.html`, which points at hashed chunks the new
// build deleted. Logging in swaps PublicTheme → PrivateTheme, which fires a
// fresh dynamic import of a chunk that is now a 404. With a bare `lazy` there
// is no retry and no reload, so react-router renders "Failed to fetch
// dynamically imported module" instead of silently recovering.
const Login = lazyWithRetry(() => import('./pages/public/login/login.page'));
const PrivateTheme = lazyWithRetry(
  () => import('./shared/theme/private/private.theme')
);
const SadminTheme = lazyWithRetry(
  () => import('./shared/theme/sadmin/sadmin.theme')
);
const PublicTheme = lazyWithRetry(
  () => import('./shared/theme/public/public.theme')
);
const NotFound = lazyWithRetry(
  () => import('./pages/public/notFound/not-found.page')
);
const PublicInvoice = lazyWithRetry(
  () => import('./pages/public/invoice/public-invoice.page')
);
const PublicProcurementPurchaseOrder = lazyWithRetry(
  () =>
    import(
      './pages/public/procurement-purchase-order/public-procurement-purchase-order.page'
    )
);
const ResellerImpersonationHandoff = lazyWithRetry(
  () =>
    import(
      './pages/public/reseller-impersonation-handoff/reseller-impersonation-handoff.page'
    )
);
const HealthStatus = lazyWithRetry(
  () => import('./pages/public/health/health.page')
);

/**
 * Route-level error surface. React Router's `errorElement` fires when
 * the loader / component tree throws (chunk fetch 404, stale menu
 * store, guard exception). Reads the error via `useRouteError` and
 * hands it to the shared branded `ErrorBoundary` for consistent UX
 * with in-tree render errors.
 */
const RouteErrorSurface = () => {
  const err = useRouteError() as Error | undefined;
  return (
    <ErrorBoundary fallback={undefined}>
      {(() => {
        throw err ?? new Error('Unknown route error');
      })()}
    </ErrorBoundary>
  );
};

const routes: RouteObject[] = [
  {
    path: '',
    element: <Navigate to={PATH.PUBLIC} />,
  },
  {
    path: '*',
    element: <Navigate to={PATH.NOT_FOUND} />,
  },
  // Public invoice route - accessible without authentication
  {
    path: '/invoice/:shareToken',
    element: createSuspendedRoute(<PublicInvoice />),
  },
  // Public procurement purchase order route - accessible without auth.
  // Reached from the WhatsApp / email share message sent by the
  // operator; lets the provider view a read-only print-style PO.
  {
    path: '/purchase-order/:shareToken',
    element: createSuspendedRoute(<PublicProcurementPurchaseOrder />),
  },
  // Reseller impersonation cross-origin landing. neoreseller mints
  // the JWT + redirects here; this page seeds the session and jumps
  // to /dashboard so the operator lands inside the target business.
  {
    path: '/reseller-impersonation-handoff',
    element: createSuspendedRoute(<ResellerImpersonationHandoff />),
  },
  // Public system-status page — no auth. Renders the independent
  // Cloudflare health.json snapshot (survives a NeoAPI/Railway outage).
  {
    path: PUBLIC_PATH.HEALTH,
    element: createSuspendedRoute(<HealthStatus />),
  },
  {
    path: PATH.PUBLIC,
    element: createSuspendedRoute(
      <GuestGuard>
        <PublicTheme />
      </GuestGuard>
    ),
    children: [...publicRoutes],
  },
  {
    path: PATH.SADMIN,
    element: createSuspendedRoute(
      <SadminGuard>
        <SadminTheme />
      </SadminGuard>
    ),
    // Render our branded ErrorBoundary instead of react-router's
    // default "Unexpected Application Error" screen when a sadmin
    // shell throws (stale menu store, chunk 404, guard exception).
    errorElement: <RouteErrorSurface />,
    children: [...sadminRoutes],
  },
  {
    path: PATH.PRIVATE,
    element: createSuspendedRoute(
      <AuthGuard>
        <ShiftGateGuard>
          <PrivateTheme />
        </ShiftGateGuard>
      </AuthGuard>
    ),
    errorElement: <RouteErrorSurface />,
    children: [...privateRoutes],
  },
  {
    path: `/*`,
    element: createSuspendedRoute(<PublicTheme />),
    children: [
      {
        path: `/*/`,
        element: createSuspendedRoute(<Login />),
      },
      {
        path: `404`,
        element: createSuspendedRoute(<NotFound />),
      },
      {
        path: `*`,
        element: createSuspendedRoute(<NotFound />),
      },
    ],
  },
];

export const rootRouter = createBrowserRouter(routes);
